import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const getBadgeVariant = (tag) => {
  switch (tag?.toLowerCase()) {
    case 'offers':
      return 'bg-foreground text-background';
    case 'more':
      return 'bg-muted text-muted-foreground';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const RecommendationSection = ({ products, onProductClick }) => {
  const scrollRef = useRef(null);
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 340;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const handleCardClick = (productId, e) => {
    // Don't navigate if clicking on buttons
    if (e.target.closest('button')) return;
    if (onProductClick) {
      onProductClick(productId);
    }
  };

  const handleAddToCart = (product, e) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.imageUrl,
      selectedSize: null,
      selectedColor: product.colors && product.colors.length > 0 ? product.colors[0] : null,
      quantity: 1,
      brand: product.brand || 'StuffLab',
      rating: product.rating,
      reviews: product.reviews,
    });
    toast.success('Added to cart!', {
      description: `${product.name} has been added to your cart.`
    });
  };

  const handleBuyNow = (product, e) => {
    e.stopPropagation();
    addToCart({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.imageUrl,
      selectedSize: null,
      selectedColor: product.colors && product.colors.length > 0 ? product.colors[0] : null,
      quantity: 1,
      brand: product.brand || 'StuffLab',
      rating: product.rating,
      reviews: product.reviews,
    });
    navigate('/cart');
  };

  if (!products || products.length === 0) return null;

  return (
    <section className="py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.h2
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="heading-2 text-foreground"
          >
            Explore our recommendations
          </motion.h2>
          
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="rounded-full border-border w-10 h-10"
              onClick={() => scroll('left')}
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="rounded-full border-border w-10 h-10"
              onClick={() => scroll('right')}
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Carousel */}
        <div 
          ref={scrollRef}
          className="flex gap-4 md:gap-6 overflow-x-auto scrollbar-thin pb-4 -mx-4 px-4 snap-x snap-mandatory"
          style={{ scrollbarWidth: 'thin' }}
        >
          {products.map((product, index) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="flex-shrink-0 w-[280px] sm:w-[320px] snap-start cursor-pointer"
              onClick={(e) => handleCardClick(product.id, e)}
            >
              <Card className="group bg-muted/30 border-0 rounded-[24px] overflow-hidden transition-all duration-300 hover:shadow-lifted hover:-translate-y-1">
                {/* Badge */}
                {product.brandTag && (
                  <div className="absolute top-4 right-4 z-10">
                    <Badge className={`${getBadgeVariant(product.brandTag)} rounded-full px-3 py-1 text-xs font-medium`}>
                      {product.brandTag}
                    </Badge>
                  </div>
                )}

                <CardContent className="p-5">
                  {/* Product Image */}
                  <div className="relative aspect-[4/3] bg-muted/50 rounded-2xl overflow-hidden mb-4">
                    <img
                      src={product.imageUrl}
                      alt={product.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  </div>

                  {/* Product Info */}
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="font-semibold text-foreground text-base leading-tight">
                        {product.name}
                      </h3>
                      <span className="font-bold text-foreground text-lg">
                        ${product.price.toFixed(2)}
                      </span>
                    </div>

                    {/* Rating */}
                    <div className="flex items-center gap-1.5">
                      <Star className="w-4 h-4 fill-warning text-warning" />
                      <span className="text-sm font-medium text-foreground">{product.rating}</span>
                      <span className="text-sm text-muted-foreground">({product.reviews} Reviews)</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 pt-2">
                      <motion.div whileTap={{ scale: 0.95 }} className="flex-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full rounded-full border-border hover:bg-muted"
                          onClick={(e) => handleAddToCart(product, e)}
                        >
                          Add to Cart
                        </Button>
                      </motion.div>
                      <motion.div whileTap={{ scale: 0.9 }} className="flex-1">
                        <Button
                          size="sm"
                          className="w-full rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                          onClick={(e) => handleBuyNow(product, e)}
                        >
                          Buy Now
                        </Button>
                      </motion.div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RecommendationSection;
